import { IEntityInfo, NSARouterMakerResult } from "namirasoft-account-react";
import { ProductUUID } from "namirasoft-api-product";
import { ContainerRegistryRow, ContainerRegistryType } from "namirasoft-container-registry";
import { IStorageLocal, NamingConvention } from "namirasoft-core";
import { NamirasoftMap } from "namirasoft-site-map";
import { BaseColumnFormatter, DateTimeFormatter, IDFormatter, JsonFormatter, StringFormatter, StringFormatterSizeType } from "namirasoft-site-react";
import { ContainerRegistryRouterProps } from "./ContainerRegistryRouterProps";
import { ContainerRegistryRoutes } from "./ContainerRegistryRoutes";

export class ContainerRegistryEntities
{
    static container_registry(props: NSARouterMakerResult & ContainerRegistryRouterProps): IEntityInfo<ContainerRegistryRow, ContainerRegistryRow>
    {
        let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

        return {
            product_id: ProductUUID.NAMIRASOFT_CONTAINER_REGISTRY,
            name: "container_registry",
            tables: [{ table: namirasoft.container_registry.tables.container_registry, required: false }],
            client: {
                getListURL: () => ContainerRegistryRoutes.ContainerRegistryList,
                getNewURL: () => ContainerRegistryRoutes.ContainerRegistryNew,
                getViewURL: (id) => ContainerRegistryRoutes.ContainerRegistryView(id),
                getEditURL: (id) => ContainerRegistryRoutes.ContainerRegistryEdit(id),
                getCopyURL: (id) => ContainerRegistryRoutes.ContainerRegistryCopy(id),
                getColumns: () =>
                {
                    return [
                        BaseColumnFormatter.getColumn(namirasoft.container_registry.tables.container_registry.columns.id, new IDFormatter(namirasoft, null)),
                        BaseColumnFormatter.getColumn(namirasoft.container_registry.tables.container_registry.columns.user_id, new IDFormatter(namirasoft, new IStorageLocal())),
                        BaseColumnFormatter.getColumn(namirasoft.container_registry.tables.container_registry.columns.workspace_id, new IDFormatter(namirasoft, new IStorageLocal())),
                        BaseColumnFormatter.getColumn(namirasoft.container_registry.tables.container_registry.columns.name, new StringFormatter()),
                        BaseColumnFormatter.getColumn(namirasoft.container_registry.tables.container_registry.columns.type, new StringFormatter(StringFormatterSizeType.FullName, NamingConvention.Pascal_Case_Space)),
                        BaseColumnFormatter.getColumn(namirasoft.container_registry.tables.container_registry.columns.meta, new JsonFormatter(StringFormatterSizeType.Description)),
                        BaseColumnFormatter.getColumn(namirasoft.container_registry.tables.container_registry.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
                        BaseColumnFormatter.getColumn(namirasoft.container_registry.tables.container_registry.columns.created_at, new DateTimeFormatter()),
                        BaseColumnFormatter.getColumn(namirasoft.container_registry.tables.container_registry.columns.updated_at, new DateTimeFormatter()),
                    ];
                },
                onCreate: async (entity: ContainerRegistryRow) =>
                {
                    let body = { ...entity, ...entity.meta };
                    delete body.id;
                    delete body.user_id;
                    delete body.workspace_id;
                    delete body.meta;
                    delete body.created_at;
                    delete body.updated_at;

                    switch (entity.type)
                    {
                        case ContainerRegistryType.AWSECR:
                            return await props.servers.container_registry.aws_ecr.Create(body);
                        case ContainerRegistryType.AzureACR:
                            return await props.servers.container_registry.azure_acr.Create(body);
                        case ContainerRegistryType.GitHubContainerRegistry:
                            return await props.servers.container_registry.git_hub_container_registry.Create(body);
                        case ContainerRegistryType.GitLabContainerRegistry:
                            return await props.servers.container_registry.git_lab_container_registry.Create(body);
                        case ContainerRegistryType.GoogleArtifactRegistry:
                            return await props.servers.container_registry.google_artifact_registry.Create(body);
                        case ContainerRegistryType.GoogleContainerRegistry:
                            return await props.servers.container_registry.google_container_registry.Create(body);
                        case ContainerRegistryType.JFrogArtifactory:
                            return await props.servers.container_registry.j_frog_artifactory.Create(body);
                        case ContainerRegistryType.Harbor:
                            return await props.servers.container_registry.harbor.Create(body);
                        case ContainerRegistryType.PrivateRegistry:
                            return await props.servers.container_registry.private_registry.Create(body);
                        case ContainerRegistryType.PublicRepository:
                            return await props.servers.container_registry.public_repository.Create(body);
                        case ContainerRegistryType.SonatypeNexus:
                            return await props.servers.container_registry.sonatype_nexus.Create(body);
                        default:
                            throw new Error("Wrong volume type");
                    }
                },
                onUpdate: async (id: string, entity: ContainerRegistryRow) =>
                {
                    let body = { ...entity, ...entity.meta };
                    delete body.id;
                    delete body.user_id;
                    delete body.workspace_id;
                    delete body.meta;
                    delete body.created_at;
                    delete body.updated_at;

                    switch (entity.type)
                    {
                        case ContainerRegistryType.AWSECR:
                            return await props.servers.container_registry.aws_ecr.Update(id, body);
                        case ContainerRegistryType.AzureACR:
                            return await props.servers.container_registry.azure_acr.Update(id, body);
                        case ContainerRegistryType.GitHubContainerRegistry:
                            return await props.servers.container_registry.git_hub_container_registry.Update(id, body);
                        case ContainerRegistryType.GitLabContainerRegistry:
                            return await props.servers.container_registry.git_lab_container_registry.Update(id, body);
                        case ContainerRegistryType.GoogleArtifactRegistry:
                            return await props.servers.container_registry.google_artifact_registry.Update(id, body);
                        case ContainerRegistryType.GoogleContainerRegistry:
                            return await props.servers.container_registry.google_container_registry.Update(id, body);
                        case ContainerRegistryType.Harbor:
                            return await props.servers.container_registry.harbor.Update(id, body);
                        case ContainerRegistryType.JFrogArtifactory:
                            return await props.servers.container_registry.j_frog_artifactory.Update(id, body);
                        case ContainerRegistryType.PrivateRegistry:
                            return await props.servers.container_registry.private_registry.Update(id, body);
                        case ContainerRegistryType.PublicRepository:
                            return await props.servers.container_registry.public_repository.Update(id, body);
                        case ContainerRegistryType.SonatypeNexus:
                            return await props.servers.container_registry.sonatype_nexus.Update(id, body);
                        default:
                            throw new Error("Wrong volume type");
                    }
                },

            },
            server: {
                value_list: props.servers.container_registry.value.List,
                list: props.servers.container_registry.container_registry.List,
                get: props.servers.container_registry.container_registry.Get,
                delete: props.servers.container_registry.container_registry.Delete,
                create: async (entity) =>
                {
                    let body = {
                        name: entity.name,
                        type: entity.type,
                        description: entity.description
                    };

                    switch (entity.type)
                    {
                        case ContainerRegistryType.AWSECR:
                            {
                                let payload = { ...body, aws_credential_id: entity.meta.aws_credential_id };
                                return await props.servers.container_registry.aws_ecr.Create(payload);
                            }
                        case ContainerRegistryType.AzureACR:
                            {
                                let payload = { ...body, azure_credential_id: entity.meta.azure_credential_id };
                                return await props.servers.container_registry.azure_acr.Create(payload);
                            }
                        case ContainerRegistryType.GitHubContainerRegistry:
                            {
                                let payload = { ...body, git_hub_credential_id: entity.meta.git_hub_credential_id };
                                return await props.servers.container_registry.git_hub_container_registry.Create(payload);
                            }
                        case ContainerRegistryType.GitLabContainerRegistry:
                            {
                                let payload = { ...body, git_lab_credential_id: entity.meta.git_lab_credential_id };
                                return await props.servers.container_registry.git_lab_container_registry.Create(payload);
                            }
                        case ContainerRegistryType.GoogleArtifactRegistry:
                            {
                                let payload = { ...body, gcp_credential_id: entity.meta.gcp_credential_id };
                                return await props.servers.container_registry.google_artifact_registry.Create(payload);
                            }
                        case ContainerRegistryType.GoogleContainerRegistry:
                            {
                                let payload = { ...body, gcp_credential_id: entity.meta.gcp_credential_id };
                                return await props.servers.container_registry.google_container_registry.Create(payload);
                            }
                        case ContainerRegistryType.Harbor:
                            {
                                let payload = { ...body, harbor_credential_id: entity.meta.harbor_credential_id };
                                return await props.servers.container_registry.harbor.Create(payload);
                            }
                        case ContainerRegistryType.JFrogArtifactory:
                            {
                                let payload = { ...body, jfrog_artifactory_credential_id: entity.meta.jfrog_artifactory_credential_id };
                                return await props.servers.container_registry.j_frog_artifactory.Create(payload);
                            }
                        case ContainerRegistryType.PrivateRegistry:
                            {
                                let payload = { ...body, basic_auth_credential_id: entity.meta.basic_auth_credential_id };
                                return await props.servers.container_registry.private_registry.Create(payload);
                            }
                        case ContainerRegistryType.PublicRepository:
                            {
                                let payload = { ...body };
                                return await props.servers.container_registry.public_repository.Create(payload);
                            }
                        case ContainerRegistryType.SonatypeNexus:
                            {
                                let payload = { ...body, aws_credential_id: entity.meta.aws_credential_id };
                                return await props.servers.container_registry.aws_ecr.Create(payload);
                            }
                        default:
                            throw new Error("Wrong volume type");
                    }
                },
                update: async (id, entity) =>
                {
                    let body = {
                        name: entity.name,
                        type: entity.type,
                        description: entity.description
                    };

                    switch (entity.type)
                    {
                        case ContainerRegistryType.AWSECR:
                            {
                                let payload = { ...body, aws_credential_id: entity.meta.aws_credential_id };
                                return await props.servers.container_registry.aws_ecr.Update(id, payload);
                            }
                        case ContainerRegistryType.AzureACR:
                            {
                                let payload = { ...body, azure_credential_id: entity.meta.azure_credential_id };
                                return await props.servers.container_registry.azure_acr.Update(id, payload);
                            }
                        case ContainerRegistryType.GitHubContainerRegistry:
                            {
                                let payload = { ...body, git_hub_credential_id: entity.meta.git_hub_credential_id };
                                return await props.servers.container_registry.git_hub_container_registry.Update(id, payload);
                            }
                        case ContainerRegistryType.GitLabContainerRegistry:
                            {
                                let payload = { ...body, git_lab_credential_id: entity.meta.git_lab_credential_id };
                                return await props.servers.container_registry.git_lab_container_registry.Update(id, payload);
                            }
                        case ContainerRegistryType.GoogleArtifactRegistry:
                            {
                                let payload = { ...body, gcp_credential_id: entity.meta.gcp_credential_id };
                                return await props.servers.container_registry.google_artifact_registry.Update(id, payload);
                            }
                        case ContainerRegistryType.GoogleContainerRegistry:
                            {
                                let payload = { ...body, gcp_credential_id: entity.meta.gcp_credential_id };
                                return await props.servers.container_registry.google_container_registry.Update(id, payload);
                            }
                        case ContainerRegistryType.Harbor:
                            {
                                let payload = { ...body, harbor_credential_id: entity.meta.harbor_credential_id };
                                return await props.servers.container_registry.harbor.Update(id, payload);
                            }
                        case ContainerRegistryType.JFrogArtifactory:
                            {
                                let payload = { ...body, jfrog_artifactory_credential_id: entity.meta.jfrog_artifactory_credential_id };
                                return await props.servers.container_registry.j_frog_artifactory.Update(id, payload);
                            }
                        case ContainerRegistryType.PrivateRegistry:
                            {
                                let payload = { ...body, basic_auth_credential_id: entity.meta.basic_auth_credential_id };
                                return await props.servers.container_registry.private_registry.Update(id, payload);
                            }
                        case ContainerRegistryType.PublicRepository:
                            {
                                let payload = { ...body };
                                return await props.servers.container_registry.public_repository.Update(id, payload);
                            }
                        case ContainerRegistryType.SonatypeNexus:
                            {
                                let payload = { ...body, sonatype_nexus_credential_id: entity.meta.sonatype_nexus_credential_id };
                                return await props.servers.container_registry.sonatype_nexus.Update(id, payload);
                            }
                        default:
                            throw new Error("Wrong volume type");
                    }
                },
            }
        }
    }
}